import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { LoaderCircle } from "lucide-react"
import { HustleupIcon } from "@images/hustleupIcon"
import LoginBackground from "@images/loginBackground.png"
import { useServicesContext } from "@modules/core/services/services.context"
import { LanguageDropdown } from "@modules/language/components/languageDropdown"

type AuthProps = {
  children: React.ReactNode
  className?: string
} & React.ComponentProps<"div">

export function AuthPage({ children, className, ...props }: AuthProps) {
  const { languageService } = useServicesContext()

  return (
    <div className={cn("relative flex max-h-screen bg-surface-background", className)} {...props}>
      <div className="hidden h-screen w-[55%] p-4.5 md:block">
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-[60px]">
          <img
            src={LoginBackground}
            className="absolute h-full w-full object-cover object-left"
            alt="Login Background"
          />
        </div>
      </div>

      <div className="flex-1 px-2.5">
        <div className="flex h-full max-w-full flex-col items-center justify-center pt-12.5 pb-10">
          <div className="absolute top-6 right-6 flex justify-end">
            <LanguageDropdown onChange={(language) => languageService.setUserLanguage(language)} />
          </div>

          <HustleupIcon color="currentColor" className="h-45 w-125 px-15 text-black xl:px-0" />

          {children}
        </div>
      </div>
    </div>
  )
}
export function AuthForm({
  children,
  className,
  ...props
}: { children: React.ReactNode } & React.ComponentProps<"form">) {
  return (
    <form className={cn("relative flex max-h-[85%] w-full flex-col items-center", className)} {...props}>
      {children}
    </form>
  )
}

export function AuthContent({ children, className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("mt-2.5 flex h-full w-[70%] flex-col gap-5 self-center", "max-sm:w-[75vw]", className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function AuthFormError({ children, className, ...props }: React.ComponentProps<"span">) {
  return (
    <Text className={cn("text-center text-textError", className)} {...props}>
      {children}
    </Text>
  )
}

type AuthSubmitButtonProps = {
  loading?: boolean
  buttonLabel: string
} & React.ComponentProps<"button">

export function AuthSubmitButton({ className, buttonLabel, loading = false, ...props }: AuthSubmitButtonProps) {
  return (
    <Button
      isLoading={loading}
      disabled={loading}
      className={cn("mt-6 justify-center", className)}
      variant="primary"
      size="lg"
      type="submit"
      {...props}
    >
      {loading && <LoaderCircle className="animate-spin" />}
      <Text fontStyle="LL">{buttonLabel}</Text>
    </Button>
  )
}

export function AuthHeader({
  title,
  subtitle,
  className,
  ...props
}: { title: string; subtitle?: string } & React.ComponentProps<"div">) {
  return (
    <div className={cn("mb-2 flex flex-col items-center gap-2", className)} {...props}>
      <Text fontStyle="HL">{title}</Text>
      {subtitle && <Text fontStyle="BM">{subtitle}</Text>}
    </div>
  )
}
